import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, ChevronRight, Check, ChevronDown,
  Activity, Building2, Landmark, Brain,
  X, TrendingUp, BarChart3, FileText, Clock,
  Bell, Star, LifeBuoy, GraduationCap, Globe,
  Filter, Gift, Layout
} from 'lucide-react';
import MarketingHeader from '../components/MarketingHeader';
import MarketConstellationBackground from '../components/MarketConstellationBackground';
import AIBrainHalo from '../components/AIBrainHalo';
import SEOHead, { getOrganizationSchema, getWebsiteSchema, getSoftwareAppSchema } from '../components/SEOHead';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_b7533cee-6cc1-4a41-9e88-4b20184fec89/artifacts/eixt3fs2_STM%20mobile%20logo.png';

// Animated background particles component - for lower sections
const ParticleBackground = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
    <div 
      className="absolute w-[800px] h-[800px] rounded-full blur-3xl opacity-10 animate-pulse"
      style={{
        background: 'radial-gradient(circle, #3B82F6 0%, transparent 70%)',
        top: '-400px',
        left: '-200px',
        animationDuration: '8s'
      }}
    />
    <div 
      className="absolute w-[600px] h-[600px] rounded-full blur-3xl opacity-8 animate-pulse"
      style={{
        background: 'radial-gradient(circle, #00F5FF 0%, transparent 70%)',
        top: '200px',
        right: '-300px',
        animationDuration: '10s',
        animationDelay: '2s'
      }}
    />
    <div 
      className="absolute inset-0 opacity-[0.012]"
      style={{
        backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                         linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
        backgroundSize: '60px 60px'
      }}
    />
  </div>
);

// ========== SYSTEM STATUS STRIP ==========
const SystemStatusStrip = () => {
  const [animatedNumbers, setAnimatedNumbers] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => setAnimatedNumbers(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const statusItems = [
    { label: 'AI ENGINE', value: 'Active', color: '#22C55E' },
    { label: 'MONITORING', value: '503', suffix: 'Stocks', color: '#3B82F6' },
    { label: 'DETECTION INTERVAL', value: '15', suffix: 'Minutes', color: '#8B5CF6' },
    { label: 'MARKET COVERAGE', value: 'S&P 500', color: '#F59E0B' },
    { label: 'LAST SCAN', value: '2', suffix: 'Minutes Ago', color: '#22C55E' },
  ];

  return (
    <section className="relative z-10 py-6 px-6" style={{ backgroundColor: '#0A0E1A' }}>
      <div 
        className="max-w-6xl mx-auto py-4 px-6 rounded-xl"
        style={{
          background: 'rgba(15, 23, 42, 0.4)',
          border: '1px solid rgba(59, 130, 246, 0.08)',
          boxShadow: '0 0 30px rgba(59, 130, 246, 0.03)'
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-center gap-3 mb-4">
          <div 
            className="w-1.5 h-1.5 rounded-full"
            style={{ background: '#22C55E', boxShadow: '0 0 6px #22C55E' }}
          />
          <span 
            className="text-[10px] font-semibold tracking-[0.2em]"
            style={{ color: '#64748B' }}
          >
            SYSTEM STATUS
          </span>
        </div>

        {/* Status Items */}
        <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
          {statusItems.map((item, idx) => (
            <div key={idx} className="text-center">
              <p 
                className="text-[9px] font-medium tracking-[0.15em] mb-1"
                style={{ color: '#475569' }}
              >
                {item.label}
              </p>
              <p 
                className={`text-sm font-semibold transition-all duration-700 ${animatedNumbers ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}
                style={{ 
                  color: item.color,
                  transitionDelay: `${idx * 100}ms`
                }}
              >
                {item.value}
                {item.suffix && <span className="text-xs font-normal ml-1" style={{ color: '#64748B' }}>{item.suffix}</span>}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ========== CURRENT ACTIVITY SNAPSHOT ==========
const CurrentActivitySnapshot = () => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const activityCards = [
    {
      ticker: 'NVDA',
      score: 84,
      volume: '2.3x',
      context: 'Unusual volume surge following institutional positioning update. Price deviation above 2 sigma threshold.',
      trend: 'up'
    },
    {
      ticker: 'TSLA',
      score: 76,
      volume: '1.8x',
      context: 'SEC filing disclosure detected. Above average options activity in near-term contracts.',
      trend: 'up'
    },
    {
      ticker: 'UNH',
      score: 71,
      volume: '1.5x',
      context: 'Accumulation pattern identified. Institutional buying pressure over last 3 sessions.',
      trend: 'neutral'
    },
    {
      ticker: 'META',
      score: 68,
      volume: '1.4x',
      context: 'Political disclosure activity detected. Moderate confidence on catalyst alignment.',
      trend: 'up'
    }
  ];

  return (
    <section className="relative z-10 py-16 px-6" style={{ backgroundColor: '#0B0F1A' }}>
      <div className="max-w-5xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-10">
          <h2 
            className="text-2xl sm:text-3xl font-bold mb-3 tracking-[-0.02em]"
            style={{ color: '#F8FAFC' }}
          >
            Current Activity Snapshot
          </h2>
          <p className="text-sm" style={{ color: '#64748B' }}>
            Live anomaly detection results from active market scanning
          </p>
        </div>

        {/* Activity Cards Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {activityCards.map((item, idx) => (
            <div
              key={idx}
              className={`p-5 rounded-xl transition-all duration-500 cursor-default ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
              style={{
                background: 'rgba(15, 23, 42, 0.5)',
                border: '1px solid rgba(59, 130, 246, 0.08)',
                transitionDelay: `${idx * 100}ms`
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.2)';
                e.currentTarget.style.boxShadow = '0 8px 30px rgba(59, 130, 246, 0.08)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.08)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <span className="text-lg font-bold" style={{ color: '#F8FAFC' }}>{item.ticker}</span>
                <span 
                  className="text-xs px-2 py-0.5 rounded"
                  style={{ 
                    background: item.trend === 'up' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(148, 163, 184, 0.1)',
                    color: item.trend === 'up' ? '#22C55E' : '#94A3B8'
                  }}
                >
                  {item.trend === 'up' ? '↑' : '-'}
                </span>
              </div>

              {/* Metrics */}
              <div className="flex items-center gap-4 mb-3">
                <div>
                  <p className="text-[9px] tracking-wider mb-0.5" style={{ color: '#475569' }}>SCORE</p>
                  <p className="text-xl font-bold" style={{ color: '#3B82F6' }}>{item.score}</p>
                </div>
                <div 
                  className="w-px h-8"
                  style={{ background: 'rgba(148, 163, 184, 0.1)' }}
                />
                <div>
                  <p className="text-[9px] tracking-wider mb-0.5" style={{ color: '#475569' }}>VOLUME</p>
                  <p className="text-base font-semibold" style={{ color: '#94A3B8' }}>{item.volume}</p>
                </div>
              </div>

              {/* Context */}
              <p className="text-xs leading-relaxed" style={{ color: '#64748B' }}>
                {item.context}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ========== FEATURES SECTION COMPONENT ==========
const FeaturesSection = () => {
  const [expandedId, setExpandedId] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  // Features data - ordered by importance (conversion-first hierarchy)
  const features = [
    // Tier 1 - Core Product
    { id: 1, icon: Activity, text: 'S&P 500 stock anomaly detection across 500+ stocks', detail: 'Continuously scans the entire S&P 500 for abnormal price and volume behavior in real time.' },
    { id: 2, icon: TrendingUp, text: 'Top 30 unusual stock activity ranked', detail: 'Automatically ranks the most significant market anomalies so you see what matters most first.' },
    { id: 3, icon: Brain, text: 'AI-generated stock catalyst breakdowns', detail: 'Uses AI to analyze recent news and explain what may be driving unusual stock movement.' },
    { id: 4, icon: FileText, text: 'Structured, readable stock market context', detail: 'Breaks down complex market activity into clear, structured insights in plain English.' },
    // Tier 2 - Intelligence Edge
    { id: 5, icon: Building2, text: 'Institutional stock positioning summaries', detail: 'Highlights recent institutional positioning shifts that could influence price direction.' },
    { id: 6, icon: Landmark, text: 'Political stock trading disclosures', detail: 'Tracks disclosed political trades and surfaces potential correlations with market activity.' },
    { id: 7, icon: Filter, text: 'Filter stock anomalies by timeframe', detail: 'Narrow your research by timeframe to match your analysis horizon.' },
    // Tier 3 - User Control
    { id: 8, icon: Bell, text: 'Set stock price and anomaly alerts', detail: 'Get notified when abnormal activity or price levels hit your custom thresholds.' },
    { id: 9, icon: Star, text: 'Add S&P 500 stocks to your watchlist', detail: 'Build and monitor a personalized list of stocks you care about most.' },
    // Tier 4 - Platform / Secondary
    { id: 10, icon: GraduationCap, text: 'Step-by-step interactive tutorial', detail: 'Learn how to interpret anomaly data with a guided in-app walkthrough.' },
    { id: 11, icon: Globe, text: 'Multiple language support', detail: 'Access the platform in multiple languages for a more inclusive experience.' },
    { id: 12, icon: Layout, text: 'Easy to navigate stock scanner design', detail: 'Designed for clarity so you can focus on research instead of complex tools.' },
    { id: 13, icon: LifeBuoy, text: 'Support tickets for help when you need it', detail: 'Reach out directly through the platform if you ever need assistance.' },
    // Always last
    { id: 14, icon: Gift, text: 'Refer friends and earn $5 per user', detail: 'Invite others and earn rewards when they join ScopeTheMarket.' },
  ];

  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handler = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  // Sweep effect - randomly highlight a card every ~3 seconds
  useEffect(() => {
    if (prefersReducedMotion) return;
    
    const interval = setInterval(() => {
      const randomIndex = Math.floor(Math.random() * features.length);
      setActiveId(features[randomIndex].id);
      
      // Clear the highlight after animation
      setTimeout(() => setActiveId(null), 800);
    }, 3000);

    return () => clearInterval(interval);
  }, [prefersReducedMotion, features.length]);

  const handleCardClick = useCallback((id) => {
    setExpandedId(expandedId === id ? null : id);
  }, [expandedId]);

  const handleKeyDown = useCallback((e, id) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardClick(id);
    }
  }, [handleCardClick]);

  return (
    <section id="features" className="relative z-10 py-24 px-6" style={{ backgroundColor: '#0E1324' }}>
      <div className="max-w-5xl mx-auto">
        <h2 
          className="text-3xl sm:text-4xl font-bold mb-12 text-center"
          style={{ color: '#F8FAFC' }}
        >
          Features
        </h2>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {features.map((item) => {
            const isExpanded = expandedId === item.id;
            const isHighlightActive = activeId === item.id;
            const IconComponent = item.icon;
            
            return (
              <div
                key={item.id}
                role="button"
                tabIndex={0}
                onClick={() => handleCardClick(item.id)}
                onKeyDown={(e) => handleKeyDown(e, item.id)}
                className="rounded-xl cursor-pointer outline-none group"
                style={{
                  background: 'rgba(15, 23, 42, 0.5)',
                  border: isHighlightActive 
                    ? '1px solid rgba(59, 130, 246, 0.6)' 
                    : '1px solid rgba(59, 130, 246, 0.08)',
                  boxShadow: isHighlightActive 
                    ? '0 0 20px rgba(59, 130, 246, 0.3), 0 0 40px rgba(139, 92, 246, 0.15)' 
                    : 'none',
                  transition: prefersReducedMotion ? 'none' : 'all 0.25s ease',
                  transform: 'translateY(0)',
                }}
                onMouseEnter={(e) => {
                  if (prefersReducedMotion) return;
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.border = '1px solid rgba(59, 130, 246, 0.4)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(59, 130, 246, 0.15), 0 0 30px rgba(139, 92, 246, 0.1)';
                  e.currentTarget.style.background = 'rgba(15, 23, 42, 0.7)';
                }}
                onMouseLeave={(e) => {
                  if (prefersReducedMotion) return;
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.border = isHighlightActive 
                    ? '1px solid rgba(59, 130, 246, 0.6)' 
                    : '1px solid rgba(59, 130, 246, 0.08)';
                  e.currentTarget.style.boxShadow = isHighlightActive 
                    ? '0 0 20px rgba(59, 130, 246, 0.3), 0 0 40px rgba(139, 92, 246, 0.15)' 
                    : 'none';
                  e.currentTarget.style.background = 'rgba(15, 23, 42, 0.5)';
                }}
                onFocus={(e) => {
                  if (prefersReducedMotion) return;
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.border = '1px solid rgba(59, 130, 246, 0.4)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(59, 130, 246, 0.15), 0 0 30px rgba(139, 92, 246, 0.1)';
                }}
                onBlur={(e) => {
                  if (prefersReducedMotion) return;
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.border = '1px solid rgba(59, 130, 246, 0.08)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div className="p-5">
                  <div className="flex items-start gap-4">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300"
                      style={{ 
                        background: isHighlightActive 
                          ? 'rgba(59, 130, 246, 0.25)' 
                          : 'rgba(59, 130, 246, 0.1)',
                        boxShadow: isHighlightActive 
                          ? '0 0 15px rgba(59, 130, 246, 0.4)' 
                          : 'none'
                      }}
                    >
                      <IconComponent 
                        className="w-5 h-5 transition-all duration-300" 
                        style={{ 
                          color: isHighlightActive ? '#60A5FA' : '#3B82F6',
                          filter: isHighlightActive ? 'drop-shadow(0 0 6px rgba(59, 130, 246, 0.6))' : 'none'
                        }} 
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium" style={{ color: '#CBD5E1' }}>
                          {item.text}
                        </span>
                        <ChevronDown 
                          className="w-4 h-4 flex-shrink-0 transition-transform duration-300"
                          style={{ 
                            color: '#64748B',
                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Expandable detail */}
                  <div 
                    className="overflow-hidden transition-all duration-300 ease-out"
                    style={{
                      maxHeight: isExpanded ? '100px' : '0px',
                      opacity: isExpanded ? 1 : 0,
                      marginTop: isExpanded ? '12px' : '0px'
                    }}
                  >
                    <p 
                      className="text-xs leading-relaxed pl-14"
                      style={{ color: '#94A3B8' }}
                    >
                      {item.detail}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA Block */}
        <div className="text-center mt-16">
          <h3 
            className="text-2xl sm:text-3xl font-bold mb-4"
            style={{ color: '#F8FAFC' }}
          >
            Ready to Spot the Next Unusual Market Move?
          </h3>
          <p 
            className="text-base mb-8 max-w-xl mx-auto"
            style={{ color: '#94A3B8' }}
          >
            Join traders using AI-powered anomaly detection to stay ahead of abnormal price and volume activity.
          </p>
          
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-base font-semibold transition-all duration-300"
            style={{
              background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
              color: 'white',
              boxShadow: '0 4px 20px rgba(59, 130, 246, 0.4), 0 0 40px rgba(59, 130, 246, 0.1)'
            }}
            onMouseEnter={(e) => {
              if (prefersReducedMotion) return;
              e.currentTarget.style.boxShadow = '0 6px 30px rgba(59, 130, 246, 0.5), 0 0 60px rgba(59, 130, 246, 0.2)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(59, 130, 246, 0.4), 0 0 40px rgba(59, 130, 246, 0.1)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Start Scanning Free
            <ArrowRight className="w-5 h-5" />
          </Link>
          
          <p 
            className="text-xs mt-4"
            style={{ color: '#64748B' }}
          >
            No credit card required.
          </p>
        </div>
      </div>
    </section>
  );
};

const Home = () => {
  // Combined structured data for homepage
  const homeStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      getOrganizationSchema(),
      getWebsiteSchema(),
      getSoftwareAppSchema()
    ]
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0B0F1A' }}>
      <SEOHead
        title="ScopeTheMarket | AI-Powered S&P 500 Stock Scanner & Anomaly Detection"
        description="ScopeTheMarket scans the S&P 500 for unusual stock activity, volume spikes, and price anomalies. Get AI-driven market insights, institutional trading signals, and political trade disclosures to research stocks smarter."
        canonical="https://scopethemarket.com/"
        ogTitle="ScopeTheMarket – AI Stock Scanner & Market Intelligence"
        ogDescription="Spot unusual S&P 500 stock moves before everyone else. AI-powered anomaly detection, institutional signals, and real-time market research."
        ogType="website"
        structuredData={homeStructuredData}
      />
      <ParticleBackground />
      
      <MarketingHeader 
        navLinks={[
          { to: '/pricing', label: 'Pricing' }
        ]}
        showFeaturesLink={true}
      />

      {/* ========== SECTION 1: HERO ========== */}
      <section 
        className="relative flex items-center justify-center px-6 md:px-8"
        style={{ minHeight: '85vh' }}
      >
        <MarketConstellationBackground />
        
        <div 
          className="absolute pointer-events-none"
          style={{
            width: '900px',
            height: '700px',
            background: 'radial-gradient(ellipse at center, rgba(59, 130, 246, 0.05) 0%, transparent 65%)',
            top: '45%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1,
          }}
        />
        
        <div className="max-w-[900px] mx-auto text-center relative" style={{ zIndex: 10 }}>
          {/* Beta Badge */}
          <div 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8"
            style={{
              background: 'rgba(59, 130, 246, 0.08)',
              border: '1px solid rgba(59, 130, 246, 0.15)',
              position: 'relative',
              zIndex: 3,
            }}
          >
            <div 
              className="w-2 h-2 rounded-full animate-pulse"
              style={{ background: '#22C55E', boxShadow: '0 0 6px #22C55E' }}
            />
            <span className="text-sm font-medium" style={{ color: '#94A3B8' }}>
              Public Beta. Improving weekly with user feedback.
            </span>
          </div>

          {/* Hero Copy with Halo */}
          <div className="relative" style={{ zIndex: 2 }}>
            <AIBrainHalo />
            
            <div className="relative px-4 md:px-8 py-6" style={{ zIndex: 2 }}>
              {/* Headline */}
              <h1 
                className="text-[2.25rem] sm:text-[2.75rem] md:text-[3.25rem] lg:text-[3.75rem] mb-6 leading-[1.1] tracking-[-0.02em]"
                style={{ 
                  color: '#F8FAFC',
                  fontFamily: "'Inter', -apple-system, sans-serif",
                  fontWeight: 800,
                  textShadow: '0 0 60px rgba(59, 130, 246, 0.15), 0 0 120px rgba(0, 229, 255, 0.08)',
                }}
              >
                <span className="block">Spot Unusual Stock Moves</span>
                <span 
                  className="block mt-2"
                  style={{ 
                    background: 'linear-gradient(90deg, #00E5FF 0%, #3B82F6 45%, #8B5CF6 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    textShadow: 'none',
                  }}
                >
                  Before Everyone Else
                </span>
              </h1>

              {/* Subheadline */}
              <p 
                className="text-base md:text-lg lg:text-xl max-w-[720px] mx-auto leading-[1.7] tracking-[-0.01em]"
                style={{ 
                  color: '#94A3B8',
                  fontWeight: 500,
                }}
              >
                ScopeTheMarket continuously scans S&P 500 stocks for abnormal price and volume behavior. 
                When unusual stock market activity appears, the system ranks it and explains what may be driving the move 
                in clear, structured language.
              </p>
            </div>
          </div>

          {/* CTAs */}
          <div 
            className="flex flex-col items-center gap-5 mt-10"
            style={{ position: 'relative', zIndex: 3 }}
          >
            {/* Primary + Secondary row */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              {/* Primary CTA */}
              <Link
                to="/app"
                className="group flex items-center gap-2 px-10 py-4 text-base font-bold rounded-xl transition-all duration-200"
                style={{
                  background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                  color: 'white',
                  boxShadow: '0 8px 32px rgba(59, 130, 246, 0.35), 0 0 0 1px rgba(59, 130, 246, 0.2)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 12px 40px rgba(59, 130, 246, 0.45), 0 0 0 1px rgba(59, 130, 246, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 32px rgba(59, 130, 246, 0.35), 0 0 0 1px rgba(59, 130, 246, 0.2)';
                }}
                data-testid="hero-cta-primary"
              >
                Start Scanning Free
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Link>

              {/* Secondary CTA */}
              <button
                onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                className="flex items-center gap-2 px-8 py-4 text-base font-semibold rounded-xl transition-all duration-200"
                style={{
                  border: '1px solid rgba(148, 163, 184, 0.3)',
                  color: '#E2E8F0',
                  background: 'rgba(255,255,255,0.02)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                  e.currentTarget.style.borderColor = 'rgba(148, 163, 184, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.02)';
                  e.currentTarget.style.borderColor = 'rgba(148, 163, 184, 0.3)';
                }}
                data-testid="hero-cta-secondary"
              >
                See How It Works
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Trust Indicators */}
          <div 
            className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm"
            style={{ color: '#64748B' }}
          >
            <span className="flex items-center gap-2">
              <span style={{ color: '#3B82F6' }}>•</span>
              Scanning S&P 500 stocks daily
            </span>
            <span className="flex items-center gap-2">
              <span style={{ color: '#3B82F6' }}>•</span>
              Top 30 ranked stock anomalies
            </span>
            <span className="flex items-center gap-2">
              <span style={{ color: '#3B82F6' }}>•</span>
              Stock news, institutional and political trades tracked
            </span>
          </div>
        </div>
      </section>

      {/* ========== SECTION 1.5: VIDEO DEMO ========== */}
      <section className="relative z-10 py-20 px-6" style={{ backgroundColor: '#0B0F1A' }}>
        {/* Background glow */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(59, 130, 246, 0.03) 0%, transparent 60%)',
          }}
        />
        
        <div className="max-w-4xl mx-auto relative">
          {/* Section Header */}
          <div className="text-center mb-10">
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 tracking-[-0.02em]"
              style={{ color: '#F8FAFC' }}
            >
              See the S&P 500 Stock Scanner in Action
            </h2>
            <p 
              className="text-base md:text-lg max-w-2xl mx-auto"
              style={{ color: '#94A3B8' }}
            >
              A quick look at how unusual stock market activity is detected, ranked and explained.
            </p>
          </div>

          {/* Video Container - Glass style */}
          <div 
            className="relative rounded-2xl overflow-hidden"
            style={{
              background: 'rgba(15, 23, 42, 0.6)',
              border: '1px solid rgba(59, 130, 246, 0.15)',
              boxShadow: '0 0 60px rgba(59, 130, 246, 0.08), 0 25px 50px -12px rgba(0, 0, 0, 0.4)',
              backdropFilter: 'blur(12px)',
            }}
          >
            {/* Inner glow overlay */}
            <div 
              className="absolute inset-0 pointer-events-none rounded-2xl"
              style={{
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, transparent 50%, rgba(139, 92, 246, 0.03) 100%)',
              }}
            />
            
            {/* Video Element */}
            <video
              className="w-full h-auto relative z-10"
              autoPlay
              muted
              loop
              playsInline
              style={{
                borderRadius: '16px',
              }}
            >
              <source 
                src="https://customer-assets.emergentagent.com/job_b5725585-4bad-4932-88c3-6a3de5ec0c1f/artifacts/3wdby8uk_Recording%202026-03-01%20152339.mp4" 
                type="video/mp4" 
              />
              Your browser does not support the video tag.
            </video>
          </div>

          {/* Trust Lines */}
          <div 
            className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm"
            style={{ color: '#64748B' }}
          >
            <span className="flex items-center gap-2">
              <Activity className="w-4 h-4" style={{ color: '#3B82F6' }} />
              Scanning S&P 500 stocks daily
            </span>
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4" style={{ color: '#22C55E' }} />
              Stock data updated every 15 minutes
            </span>
            <span className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" style={{ color: '#8B5CF6' }} />
              Top 30 stock anomalies ranked
            </span>
          </div>

          {/* CTA Button */}
          <div className="mt-8 text-center">
            <Link
              to="/app"
              className="group inline-flex items-center gap-2 px-8 py-3.5 text-base font-bold rounded-xl transition-all duration-200"
              style={{
                background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                color: 'white',
                boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(59, 130, 246, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 32px rgba(59, 130, 246, 0.3)';
              }}
              data-testid="video-section-cta"
            >
              Start Scanning Free
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* ========== SYSTEM STATUS STRIP ========== */}
      <SystemStatusStrip />

      {/* ========== CURRENT ACTIVITY SNAPSHOT ========== */}
      <CurrentActivitySnapshot />

      {/* ========== SECTION 3: HOW IT WORKS ========== */}
      <section id="how-it-works" className="relative z-10 py-24 px-6" style={{ backgroundColor: '#0E1324' }}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#F8FAFC' }}>
              How It Works
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {[
              {
                number: '1',
                title: 'We scan S&P 500 stocks continuously'
              },
              {
                number: '2',
                title: 'We detect abnormal stock price and volume behavior'
              },
              {
                number: '3',
                title: 'We analyze the likely catalyst behind the stock move'
              }
            ].map((step, index) => (
              <div 
                key={index}
                className="text-center"
                data-testid={`how-it-works-step-${index + 1}`}
              >
                <div 
                  className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5 text-xl font-bold"
                  style={{
                    background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                    color: 'white',
                    boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)'
                  }}
                >
                  {step.number}
                </div>
                
                <p className="text-lg font-medium leading-relaxed" style={{ color: '#CBD5E1' }}>
                  {step.title}
                </p>
              </div>
            ))}
          </div>

          {/* Closing line */}
          <p 
            className="text-center text-lg font-medium"
            style={{ color: '#94A3B8' }}
          >
            You get ranked unusual stock activity with structured context, not noise.
          </p>
        </div>
      </section>

      {/* ========== SECTION 4: WHY THIS MATTERS ========== */}
      <section className="relative z-10 py-24 px-6" style={{ backgroundColor: '#0B0F1A' }}>
        <div className="max-w-3xl mx-auto">
          <h2 
            className="text-3xl sm:text-4xl md:text-[2.5rem] font-bold mb-10 tracking-[-0.02em] leading-tight"
            style={{ color: '#F8FAFC' }}
          >
            Most stock traders only notice the move after it starts trending.
          </h2>
          
          <div className="space-y-4 mb-10">
            {[
              "A stock jumps and you are searching for answers",
              "Stock market news is long and difficult to interpret",
              "Stock charts feel overwhelming",
              "By the time you understand the story, the stock move has already happened"
            ].map((problem, idx) => (
              <div 
                key={idx}
                className="flex items-start gap-4 text-lg"
                style={{ color: '#94A3B8' }}
              >
                <span 
                  className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ background: '#EF4444' }}
                />
                <span>{problem}</span>
              </div>
            ))}
          </div>
          
          <p 
            className="text-xl md:text-2xl font-semibold leading-relaxed"
            style={{ color: '#E2E8F0' }}
          >
            ScopeTheMarket is built for the first signs of abnormal activity, not the aftermath.
          </p>
        </div>
      </section>

      {/* ========== SECTION 5: FEATURES (ENHANCED) ========== */}
      <FeaturesSection />

      {/* ========== SECTION 6: IDENTITY CLARITY ========== */}
      <section className="relative z-10 py-24 px-6" style={{ backgroundColor: '#0B0F1A' }}>
        <div className="max-w-3xl mx-auto text-center">
          <h2 
            className="text-3xl sm:text-4xl font-bold mb-10"
            style={{ color: '#F8FAFC' }}
          >
            This is not a stock charting platform.
          </h2>

          <div className="space-y-5 mb-12">
            {[
              "We do not overload you with stock indicators.",
              "We do not sell stock trading advice.",
              "We do not predict stock prices."
            ].map((text, idx) => (
              <div 
                key={idx}
                className="flex items-center justify-center gap-3 text-lg"
                style={{ color: '#64748B' }}
              >
                <X className="w-5 h-5" style={{ color: '#475569' }} />
                <span>{text}</span>
              </div>
            ))}
          </div>

          <p 
            className="text-xl md:text-2xl font-semibold leading-relaxed"
            style={{ color: '#E2E8F0' }}
          >
            We highlight statistically unusual stock behavior and explain what may be driving it, 
            so you can decide what to do next.
          </p>
        </div>
      </section>

      {/* ========== SECTION 7: PRICING ========== */}
      <section className="relative z-10 py-24 px-6" style={{ backgroundColor: '#0E1324' }}>
        <div className="max-w-4xl mx-auto">
          {/* Beta Transparency Banner */}
          <div 
            className="text-center mb-8 p-4 rounded-xl"
            style={{
              background: 'rgba(59, 130, 246, 0.05)',
              border: '1px solid rgba(59, 130, 246, 0.1)'
            }}
          >
            <p className="text-sm font-medium" style={{ color: '#60A5FA' }}>
              S&P 500 Stock Scanner - Public Beta Pricing
            </p>
            <p className="text-sm mt-1" style={{ color: '#64748B' }}>
              We are actively refining stock anomaly detection accuracy and expanding the intelligence engine. 
              Early users keep their rate as we grow.
            </p>
          </div>

          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#F8FAFC' }}>
              Simple Stock Scanner Pricing
            </h2>
            <p className="text-base md:text-lg" style={{ color: '#94A3B8' }}>
              Start scanning S&P 500 stocks free, upgrade when you are ready
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Free Plan */}
            <div 
              className="p-8 rounded-2xl"
              style={{
                background: 'rgba(15, 23, 42, 0.6)',
                border: '1px solid rgba(148, 163, 184, 0.1)'
              }}
              data-testid="pricing-free-card"
            >
              <h3 className="text-xl font-semibold mb-2" style={{ color: '#F8FAFC' }}>Free</h3>
              <p className="text-4xl font-bold mb-1" style={{ color: '#F8FAFC' }}>
                $0 <span className="text-base font-normal" style={{ color: '#64748B' }}>/month</span>
              </p>
              <p className="text-sm mb-6" style={{ color: '#64748B' }}>Start scanning stocks at no cost</p>
              
              <div className="space-y-3 mb-8">
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                  <span className="text-sm" style={{ color: '#CBD5E1' }}>Limited S&P 500 stock scans</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                  <span className="text-sm" style={{ color: '#CBD5E1' }}>Up to 5 stock detail views</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                  <span className="text-sm" style={{ color: '#CBD5E1' }}>Basic stock market overview</span>
                </div>
              </div>

              <Link
                to="/app"
                className="block w-full py-3 rounded-xl text-center text-sm font-medium transition-colors"
                style={{
                  background: 'rgba(148, 163, 184, 0.1)',
                  border: '1px solid rgba(148, 163, 184, 0.2)',
                  color: '#E2E8F0'
                }}
              >
                Start Free
              </Link>
            </div>

            {/* Pro Plan */}
            <div 
              className="p-8 rounded-2xl relative"
              style={{
                background: 'rgba(15, 23, 42, 0.6)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                boxShadow: '0 0 60px rgba(59, 130, 246, 0.1)'
              }}
              data-testid="pricing-pro-card"
            >
              <div 
                className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-medium"
                style={{
                  background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                  color: 'white'
                }}
              >
                Recommended
              </div>

              <h3 className="text-xl font-semibold mb-2 mt-2" style={{ color: '#F8FAFC' }}>Pro</h3>
              <p className="text-4xl font-bold mb-1" style={{ color: '#F8FAFC' }}>
                $14.99 <span className="text-base font-normal" style={{ color: '#64748B' }}>/month</span>
              </p>
              <p className="text-sm mb-6" style={{ color: '#64748B' }}>Full access to all stock scanner features</p>
              
              <div className="space-y-3 mb-8">
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5" style={{ color: '#3B82F6' }} />
                  <span className="text-sm" style={{ color: '#CBD5E1' }}>Full S&P 500 anomaly list (30 stocks)</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5" style={{ color: '#3B82F6' }} />
                  <span className="text-sm" style={{ color: '#CBD5E1' }}>Real-time stock alerts</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5" style={{ color: '#3B82F6' }} />
                  <span className="text-sm" style={{ color: '#CBD5E1' }}>Unlimited stock detail views</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5" style={{ color: '#3B82F6' }} />
                  <span className="text-sm" style={{ color: '#CBD5E1' }}>Full stock catalyst breakdown</span>
                </div>
              </div>

              <Link
                to="/pricing"
                className="block w-full py-3 rounded-xl text-center text-sm font-medium transition-all duration-200"
                style={{
                  background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                  color: 'white',
                  boxShadow: '0 4px 20px rgba(59, 130, 246, 0.25)'
                }}
              >
                Start Beta Access
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SECTION 8: FINAL CTA ========== */}
      <section className="relative z-10 py-24 px-6" style={{ backgroundColor: '#0B0F1A' }}>
        <div className="max-w-2xl mx-auto text-center">
          <h2 
            className="text-3xl sm:text-4xl font-bold mb-10"
            style={{ color: '#F8FAFC' }}
          >
            Start spotting unusual S&P 500 stock activity with clarity.
          </h2>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {/* Primary Button */}
            <Link
              to="/app"
              className="group flex items-center gap-2 px-10 py-4 text-lg font-bold rounded-xl transition-all duration-200"
              style={{
                background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                color: 'white',
                boxShadow: '0 8px 32px rgba(59, 130, 246, 0.35)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(59, 130, 246, 0.45)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 32px rgba(59, 130, 246, 0.35)';
              }}
              data-testid="final-cta-primary"
            >
              Start Scanning Free
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </Link>

            {/* Secondary Button */}
            <Link
              to="/pricing"
              className="flex items-center gap-2 px-8 py-4 text-base font-semibold rounded-xl transition-all duration-200"
              style={{
                border: '1px solid rgba(148, 163, 184, 0.3)',
                color: '#E2E8F0',
                background: 'rgba(255,255,255,0.02)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                e.currentTarget.style.borderColor = 'rgba(148, 163, 184, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.02)';
                e.currentTarget.style.borderColor = 'rgba(148, 163, 184, 0.3)';
              }}
              data-testid="final-cta-secondary"
            >
              View Pricing
            </Link>
          </div>

          {/* Closing line */}
          <p 
            className="mt-8 text-sm"
            style={{ color: '#64748B' }}
          >
            Built for retail stock investors who want structure, speed and context.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 py-16 px-6" style={{ backgroundColor: '#0E1324', borderTop: '1px solid rgba(148, 163, 184, 0.1)' }}>
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            {/* Brand */}
            <div className="md:col-span-1">
              <Link to="/" className="flex items-center gap-3 mb-4">
                <img 
                  src={LOGO_URL} 
                  alt="ScopeTheMarket S&P 500 Stock Scanner" 
                  className="h-8 w-auto"
                  style={{ filter: 'drop-shadow(0 0 6px rgba(59, 130, 246, 0.4))' }}
                />
                <span className="text-lg font-semibold" style={{ color: '#F8FAFC' }}>ScopeTheMarket</span>
              </Link>
              <p className="text-sm leading-relaxed" style={{ color: '#64748B' }}>
                S&P 500 stock anomaly detection and market intelligence for retail traders.
              </p>
            </div>

            {/* Product */}
            <div>
              <h4 className="text-sm font-semibold mb-4" style={{ color: '#F8FAFC' }}>Stock Scanner</h4>
              <div className="space-y-3">
                <Link to="/app" className="block text-sm transition-colors hover:text-white" style={{ color: '#64748B' }}>Stock Dashboard</Link>
                <Link to="/pricing" className="block text-sm transition-colors hover:text-white" style={{ color: '#64748B' }}>Pricing</Link>
                <Link to="/blog" className="block text-sm transition-colors hover:text-white" style={{ color: '#64748B' }}>Stock Market Blog</Link>
              </div>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-sm font-semibold mb-4" style={{ color: '#F8FAFC' }}>Company</h4>
              <div className="space-y-3">
                <Link to="/about" className="block text-sm transition-colors hover:text-white" style={{ color: '#64748B' }}>About</Link>
                <Link to="/blog" className="block text-sm transition-colors hover:text-white" style={{ color: '#64748B' }}>Blog</Link>
              </div>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-sm font-semibold mb-4" style={{ color: '#F8FAFC' }}>Legal</h4>
              <div className="space-y-3">
                <Link to="/terms" className="block text-sm transition-colors hover:text-white" style={{ color: '#64748B' }}>Terms of Service</Link>
                <Link to="/privacy" className="block text-sm transition-colors hover:text-white" style={{ color: '#64748B' }}>Privacy Policy</Link>
              </div>
            </div>
          </div>

          <div className="pt-8" style={{ borderTop: '1px solid rgba(148, 163, 184, 0.1)' }}>
            {/* Financial Disclaimer */}
            <div className="mb-6 px-4">
              <p className="text-xs leading-relaxed text-center" style={{ color: '#475569' }}>
                <strong style={{ color: '#64748B' }}>Disclaimer:</strong> ScopeTheMarket provides market research and intelligence for educational and informational purposes only. 
                Nothing on this platform constitutes investment advice, trading recommendations, or a solicitation to buy or sell securities. 
                Past performance does not guarantee future results. Users are solely responsible for their own investment decisions and should 
                conduct their own research or consult a qualified financial advisor. ScopeTheMarket and its affiliates are not liable for any 
                financial losses incurred from the use of this platform.
              </p>
            </div>
            <p className="text-sm text-center" style={{ color: '#475569' }}>
              © {new Date().getFullYear()} ScopeTheMarket. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
